import * as React from 'react';
import * as TabsPrimitive from '@radix-ui/react-tabs';

import { cn } from 'src/shared/helpers/utils';

// Helix design system 3.0 "Tabs": a grey track, with the selected tab as a white raised pill.
// Three sizes: `large` (14px text, room for an icon), `default` (regular, 14px) and `small` (12px text).
type TabsSize = 'large' | 'default' | 'small';

const TabsSizeContext = React.createContext<TabsSize>('default');

const Tabs = ({ size = 'default', ...props }: React.ComponentPropsWithoutRef<typeof TabsPrimitive.Root> & { size?: TabsSize }) => (
    <TabsSizeContext.Provider value={size}>
        <TabsPrimitive.Root {...props} />
    </TabsSizeContext.Provider>
);

const listBase = 'inline-flex items-center bg-grey-0';
const listSizes: Record<TabsSize, string> = {
    large: 'rounded-xl p-1',
    default: 'rounded-xl p-1',
    small: 'rounded-md p-0.5',
};

const triggerBase =
    'inline-flex shrink-0 items-center justify-center whitespace-nowrap font-medium text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:drop-shadow-sm';
const triggerSizes: Record<TabsSize, string> = {
    large: 'min-h-8 gap-2 rounded-card px-2.5 py-1.5 text-sm',
    default: 'h-7 gap-1.5 rounded-card px-2 py-1 text-sm',
    small: 'h-5 gap-1 rounded-md px-1.5 py-px text-xs',
};

const TabsList = React.forwardRef<React.ElementRef<typeof TabsPrimitive.List>, React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>>(
    ({ className, ...props }, ref) => {
        const size = React.useContext(TabsSizeContext);
        return <TabsPrimitive.List ref={ref} className={cn(listBase, listSizes[size], className)} {...props} />;
    }
);
TabsList.displayName = TabsPrimitive.List.displayName;

const TabsTrigger = React.forwardRef<
    React.ElementRef<typeof TabsPrimitive.Trigger>,
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>
>(({ className, ...props }, ref) => {
    const size = React.useContext(TabsSizeContext);
    return <TabsPrimitive.Trigger ref={ref} className={cn(triggerBase, triggerSizes[size], className)} {...props} />;
});
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;

const TabsContent = React.forwardRef<
    React.ElementRef<typeof TabsPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>
>(({ className, ...props }, ref) => (
    <TabsPrimitive.Content
        ref={ref}
        className={cn('mt-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring', className)}
        {...props}
    />
));
TabsContent.displayName = TabsPrimitive.Content.displayName;

export { Tabs, TabsList, TabsTrigger, TabsContent };
